import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteField,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../services/firebase";

export const PROJECT_ROLES = ["Editor", "Viewer"];
export const PROJECT_STATUSES = ["Active", "Quoting", "In Progress", "On Hold", "Complete", "Archived"];

const clean = (value) => String(value || "").trim();
const emailKey = (value) => clean(value).toLowerCase();

export const createCollaborativeProject = async (project, user) => {
  if (!user?.uid) throw new Error("A valid login is required.");
  if (!clean(project.name)) throw new Error("Project name is required.");

  return addDoc(collection(db, "collaborativeProjects"), {
    name: clean(project.name),
    description: clean(project.description),
    status: project.status || "Active",
    ownerId: user.uid,
    ownerEmail: user.email || "",
    ownerName: user.displayName || user.email || "Owner",
    collaboratorIds: [],
    collaboratorEmails: [],
    collaboratorRoles: {},
    pendingInviteEmails: [],
    linkedCalculators: [],
    linkedManufacturingPlans: [],
    linkedSops: [],
    linkedSetupSheets: [],
    linkedInspectionSheets: [],
    linkedFiles: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const getCollaborativeProjectsForUser = async (user) => {
  if (!user?.uid) return [];
  const owned = await getDocs(
    query(collection(db, "collaborativeProjects"), where("ownerId", "==", user.uid), limit(50))
  );
  const shared = await getDocs(
    query(collection(db, "collaborativeProjects"), where("collaboratorIds", "array-contains", user.uid), limit(50))
  );
  const projects = [...owned.docs, ...shared.docs].map((item) => ({ id: item.id, ...item.data() }));
  return Array.from(new Map(projects.map((project) => [project.id, project])).values())
    .sort((a, b) => String(b.updatedAt?.seconds || "").localeCompare(String(a.updatedAt?.seconds || "")));
};

export const getPendingProjectInvites = async (user) => {
  const invitedEmail = emailKey(user?.email);
  if (!invitedEmail) return [];
  const snapshot = await getDocs(
    query(
      collection(db, "projectInvites"),
      where("invitedEmail", "==", invitedEmail),
      where("status", "==", "Pending"),
      limit(50)
    )
  );
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
};

export const inviteProjectCollaborator = async (project, invite, user) => {
  if (!user?.uid || project.ownerId !== user.uid) {
    throw new Error("Only the project owner can invite collaborators.");
  }
  const invitedEmail = emailKey(invite.email);
  if (!invitedEmail) throw new Error("Invite email is required.");
  const role = PROJECT_ROLES.includes(invite.role) ? invite.role : "Viewer";

  const inviteRef = await addDoc(collection(db, "projectInvites"), {
    projectId: project.id,
    projectName: project.name,
    invitedEmail,
    invitedRole: role,
    status: "Pending",
    invitedBy: user.uid,
    invitedByEmail: user.email || "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await updateDoc(doc(db, "collaborativeProjects", project.id), {
    pendingInviteEmails: arrayUnion(invitedEmail),
    updatedAt: serverTimestamp(),
  });

  return inviteRef;
};

export const acceptProjectInvite = async (invite, user) => {
  if (emailKey(user?.email) !== invite.invitedEmail) {
    throw new Error("This invite belongs to a different email address.");
  }
  const role = PROJECT_ROLES.includes(invite.invitedRole) ? invite.invitedRole : "Viewer";
  await updateDoc(doc(db, "projectInvites", invite.id), {
    status: "Accepted",
    acceptedBy: user.uid,
    updatedAt: serverTimestamp(),
  });
  await updateDoc(doc(db, "collaborativeProjects", invite.projectId), {
    collaboratorIds: arrayUnion(user.uid),
    collaboratorEmails: arrayUnion(invite.invitedEmail),
    [`collaboratorRoles.${user.uid}`]: role,
    pendingInviteEmails: arrayRemove(invite.invitedEmail),
    updatedAt: serverTimestamp(),
  });
};

export const declineProjectInvite = async (invite, user) => {
  if (emailKey(user?.email) !== invite.invitedEmail) {
    throw new Error("This invite belongs to a different email address.");
  }
  await updateDoc(doc(db, "projectInvites", invite.id), {
    status: "Declined",
    updatedAt: serverTimestamp(),
  });
  await updateDoc(doc(db, "collaborativeProjects", invite.projectId), {
    pendingInviteEmails: arrayRemove(invite.invitedEmail),
    updatedAt: serverTimestamp(),
  });
};

export const addProjectActivityMessage = async (project, message, user) => {
  const text = clean(message);
  if (!text) throw new Error("Message is required.");
  const role = project.collaboratorRoles?.[user?.uid];
  const canPost = project.ownerId === user?.uid || role === "Editor";
  if (!canPost) throw new Error("Viewer access cannot add project notes.");
  return addDoc(collection(db, "collaborativeProjects", project.id, "activity"), {
    message: text,
    authorId: user.uid,
    authorName: user.displayName || user.email || "Project collaborator",
    authorEmail: user.email || "",
    createdAt: serverTimestamp(),
  });
};

export const getProjectActivityMessages = async (projectId) => {
  if (!projectId) return [];
  const snapshot = await getDocs(
    query(
      collection(db, "collaborativeProjects", projectId, "activity"),
      orderBy("createdAt", "desc"),
      limit(50)
    )
  );
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
};

export const updateProjectCollaboratorRole = async (project, collaboratorId, role, user) => {
  if (project.ownerId !== user?.uid) throw new Error("Only the project owner can manage collaborator roles.");
  if (!PROJECT_ROLES.includes(role)) throw new Error("Invalid collaborator role.");
  return updateDoc(doc(db, "collaborativeProjects", project.id), {
    [`collaboratorRoles.${collaboratorId}`]: role,
    updatedAt: serverTimestamp(),
  });
};

export const removeProjectCollaborator = async (project, collaboratorId, email, user) => {
  if (project.ownerId !== user?.uid) throw new Error("Only the project owner can remove collaborators.");
  return updateDoc(doc(db, "collaborativeProjects", project.id), {
    collaboratorIds: arrayRemove(collaboratorId),
    collaboratorEmails: email ? arrayRemove(email) : project.collaboratorEmails || [],
    [`collaboratorRoles.${collaboratorId}`]: deleteField(),
    updatedAt: serverTimestamp(),
  });
};

export const updateCollaborativeProject = (project, updates, user) => {
  const role = project.collaboratorRoles?.[user?.uid];
  const canEdit = project.ownerId === user?.uid || role === "Editor";
  if (!canEdit) throw new Error("You do not have edit access to this project.");
  return updateDoc(doc(db, "collaborativeProjects", project.id), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};
